import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthenticatedFetch } from '../utils/api';
import { showSuccessToast, showErrorToast } from '../utils/toast';
import { formatDate } from '../utils/dateFormatter';
import {
  formatSize, formatBytesAsSize, formatCountdown,
  formatRelativeDate, formatShortDate,
  triggerDownload, getFileBadgeTone, getFileStatusLabel,
} from '../utils/imageUtils';
import useSearch from '../hooks/useSearch';
import useManualUpload from '../hooks/useManualUpload';
import useFileDropZone, { ACCEPTED_LABEL, MAX_FILES } from '../hooks/useFileDropZone';
import OptimizationTypeSelector from './shared/OptimizationTypeSelector';
import CompareModal from './shared/CompareModal';
import PaginationFooter from './shared/PaginationFooter';

// ─── Constants ────────────────────────────────────────────────────────────────

const RECENTS_PAGE_SIZE = 10;

function getProfileDescription(type) {
  switch (type) {
    case 'moderate': return 'Quality: 60% · Max width: 1400px';
    case 'extreme':  return 'Quality: 40% · Max width: 1000px';
    default:         return 'Quality: 75% · Max width: 1800px';
  }
}

// ─── ManualUploadThumbnail ───────────────────────────────────────────────────

function ManualUploadThumbnail({ sessionId, file, fetchRef }) {
  // Use CDN URLs directly if available (avoids per-file API call)
  const directSrc = file.shopifyOptimizedFileUrl || file.shopifyOriginalFileUrl || null;
  const [src, setSrc] = useState(directSrc);
  const [loading, setLoading] = useState(!directSrc);

  useEffect(() => {
    // Only fetch via compare endpoint when CDN URLs are not already in the file data
    if (directSrc) return;
    let isMounted = true;
    const loadThumbnail = async () => {
      try {
        const res = await fetchRef.current(`/api/manual-upload/compare/${sessionId}/${encodeURIComponent(file.safeName)}`);
        const data = await res.json();
        if (isMounted && data.success) {
          setSrc(data.optimizedUrl || data.originalUrl);
        }
      } catch (err) {
        console.error('[ManualUploadThumbnail] Failed to load:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadThumbnail();
    return () => { isMounted = false; };
  }, [sessionId, file.safeName, fetchRef, directSrc]);

  if (loading) {
    return <div className="shimmer-loader" style={{ width: 40, height: 40, borderRadius: 4 }} />;
  }

  return (
    <div style={{ width: 40, height: 40, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f6f6f7', border: '1px solid #e1e3e5', borderRadius: 4, flexShrink: 0 }}>
      {src ? (
        <img
          src={src}
          alt=""
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : (
        <span style={{ fontSize: 18 }}>🖼️</span>
      )}
    </div>
  );
}

// ─── RecentUploads ────────────────────────────────────────────────────────────

function RecentUploads({ refreshKey, onCompare }) {
  const { authenticatedFetch } = useAuthenticatedFetch();
  // Stable ref — keeps authenticatedFetch current without making it a useEffect/useCallback dep
  const fetchRef = useRef(authenticatedFetch);
  useEffect(() => { fetchRef.current = authenticatedFetch; }, [authenticatedFetch]);

  const [sessions, setSessions]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [localRefreshCount, setLocalRefreshCount] = useState(0);
  const [error, setError]               = useState(null);
  const [expanded, setExpanded]         = useState({});
  const [downloading, setDownloading]   = useState(null);
  const [currentPage, setCurrentPage]   = useState(1);
  const [pageInfo, setPageInfo]         = useState(null);

  const { searchQuery, debouncedSearchQuery, handleSearchChange, clearSearch } = useSearch(500, () => {
    setCurrentPage(1);
  });

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ page: currentPage, pageSize: RECENTS_PAGE_SIZE });
    if (debouncedSearchQuery) params.set('search', debouncedSearchQuery);

    fetchRef.current(`/api/manual-upload/recents?${params}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        if (cancelled) return;
        if (data.success) {
          const list = data.sessions || [];
          setSessions(list);
          setPageInfo(data.pageInfo || null);
          if (list.length > 0 && currentPage === 1 && !debouncedSearchQuery) {
            setExpanded({ [list[0].sessionId]: true });
          }
        } else {
          setError('Could not load recent uploads.');
        }
      })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load recent uploads.'); })
      .finally(() => { if (!cancelled) { setLoading(false); } });

    return () => { cancelled = true; };
  // authenticatedFetch intentionally excluded — accessed via fetchRef to prevent infinite loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, currentPage, debouncedSearchQuery, localRefreshCount]);

  const toggleExpand = (sid) => setExpanded(prev => ({ ...prev, [sid]: !prev[sid] }));

  const doDownload = useCallback(async (key, url, filename, errMsg) => {
    if (downloading === key) return;
    setDownloading(key);
    try {
      const r = await fetchRef.current(url);
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        showErrorToast(data.message || errMsg);
        return;
      }

      // Check content type to see if it's a ZIP or a JSON response with a URL
      const contentType = r.headers.get('Content-Type');
      if (contentType && contentType.includes('application/zip')) {
        const blob = await r.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        triggerDownload(blobUrl, filename || 'optimized_images.zip');
        window.URL.revokeObjectURL(blobUrl);
      } else {
        const data = await r.json();
        if (!data.success || !data.url) { showErrorToast(data.message || errMsg); return; }
        triggerDownload(data.url, filename || data.filename || 'download');
      }
    } catch (err) {
      showErrorToast(err.message);
    } finally {
      setDownloading(null);
    }
  }, [downloading]);

  const handleDownloadFile = (sessionId, file) =>
    doDownload(
      `${sessionId}:${file.safeName}`,
      `/api/manual-upload/download/${sessionId}/${encodeURIComponent(file.safeName)}`,
      `${file.safeName}_optimized.${file.ext}`,
      'Could not generate download link.'
    );

  const handleDownloadZip = (sessionId) =>
    doDownload(
      `zip:${sessionId}`,
      `/api/manual-upload/download-zip/${sessionId}`,
      null,
      'Could not generate ZIP.'
    );

  const handleCompare = async (sessionId, file) => {
    try {
      const r    = await fetchRef.current(`/api/manual-upload/compare/${sessionId}/${encodeURIComponent(file.safeName)}`);
      const data = await r.json();
      if (!r.ok || !data.success) { showErrorToast(data.message || 'Could not load compare images.'); return; }
      onCompare({
        originalName:    file.imageName,
        originalUrl:     data.originalUrl,
        optimizedUrl:    data.optimizedUrl,
        originalSizeKb:  file.originalSizeKb,
        optimizedSizeKb: file.optimizedSizeKb,
        savingsPercent:  file.savingsPercent,
      });
    } catch (err) {
      showErrorToast(`Compare failed: ${err.message}`);
    }
  };

  const isSearching = debouncedSearchQuery.trim() !== '';

  return (
    <s-box borderRadius="base" background="base">
      <div className="sticky-header">
        <s-box paddingInline="base" paddingBlock="small" borderStyle="none none solid none" border="base">
          <s-stack gap="small">
            <s-stack direction="inline" gap="base" alignItems="center" justifyContent="space-between">
              <s-heading size="small">Recent Uploads</s-heading>
            </s-stack>

            <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
              <s-search-field
                label="Search"
                labelAccessibilityVisibility="exclusive"
                placeholder="Filter by file name…"
                value={searchQuery}
                onInput={handleSearchChange}
              />
              <s-button
                variant="secondary"
                onClick={() => { setLocalRefreshCount(c => c + 1); setCurrentPage(1); }}
                disabled={loading}
              >
                Refresh
              </s-button>
            </s-grid>
          </s-stack>
        </s-box>
      </div>

      {!loading && isSearching && sessions.length > 0 && (
        <s-box paddingInline="base" paddingBlockStart="small">
          <s-banner tone="info">
            <s-stack direction="inline" gap="small" alignItems="center">
              <s-text>Showing search results for "{debouncedSearchQuery}".</s-text>
              <s-button variant="tertiary" onClick={clearSearch}>Clear search</s-button>
            </s-stack>
          </s-banner>
        </s-box>
      )}

      {loading && (
        <s-stack gap="none">
          {[1, 2, 3].map(i => (
            <s-box key={i} borderStyle="none none solid none" border="base" padding="base">
              <s-grid gridTemplateColumns="auto 1fr auto" gap="base" alignItems="center">
                <div className="shimmer-loader" style={{ height: 24, width: 24 }} />
                <s-stack gap="small">
                  <div className="shimmer-loader" style={{ height: 18, width: '50%' }} />
                  <div className="shimmer-loader" style={{ height: 14, width: '30%' }} />
                </s-stack>
                <div className="shimmer-loader" style={{ height: 32, width: 120 }} />
              </s-grid>
            </s-box>
          ))}
        </s-stack>
      )}

      {!loading && error && (
        <s-box padding="base">
          <s-banner tone="critical"><s-text>{error}</s-text></s-banner>
        </s-box>
      )}

      {!loading && !error && sessions.length === 0 && (
        <s-box padding="base">
          <s-empty-state
            heading={isSearching ? 'No matching uploads' : 'No manual uploads yet'}
            action-content={isSearching ? 'Clear Search' : undefined}
            onAction={isSearching ? clearSearch : undefined}
          >
            <s-text>
              {isSearching
                ? `No files found matching "${debouncedSearchQuery}". Try a different name.`
                : 'Upload images above to get started. Optimized files are saved permanently.'}
            </s-text>
          </s-empty-state>
        </s-box>
      )}

      {!loading && !error && sessions.length > 0 && (
        <s-stack gap="none">
          {sessions.map(session => {
              const isExpanded     = !!expanded[session.sessionId];
              const completedCount = session.completedFiles || 0;
              const failedCount    = session.failedFiles    || 0;
              const totalCount     = session.totalFiles     || session.files.length;
              const allGood        = completedCount === totalCount && failedCount === 0;
              const downloadableFiles = session.files.filter(f => f.hasDownload);
              const completionTone    = allGood ? 'success' : failedCount > 0 ? 'critical' : 'attention';
              const zipKey            = `zip:${session.sessionId}`;

              return (
                <s-box key={session.sessionId} borderStyle="none none solid none" border="base">
                  <s-clickable
                    paddingInline="base"
                    paddingBlock="small"
                    onClick={() => toggleExpand(session.sessionId)}
                  >
                    <s-grid gridTemplateColumns="auto 1fr auto" gap="base" alignItems="center">
                      <s-button
                        variant="tertiary"
                        icon={isExpanded ? 'chevron-down' : 'chevron-right'}
                        onClick={(e) => { e.stopPropagation(); toggleExpand(session.sessionId); }}
                      />

                      <s-stack direction="inline" gap="base" alignItems="center">
                        <s-stack gap="none">
                          <s-text type="strong" size="small">{formatShortDate(session.createdAt)}</s-text>
                          <s-text color="subdued" size="small">{formatRelativeDate(session.createdAt)}</s-text>
                        </s-stack>
                        <s-badge tone="info">
                          {session.optimizationType.charAt(0).toUpperCase() + session.optimizationType.slice(1)}
                        </s-badge>
                        <s-badge tone={completionTone}>
                          {completedCount}/{totalCount} optimized
                        </s-badge>
                        {failedCount > 0 && (
                          <s-badge tone="critical">{failedCount} failed</s-badge>
                        )}
                      </s-stack>

                      <s-stack direction="inline" gap="small" onClick={e => e.stopPropagation()}>
                        {downloadableFiles.length > 1 && (
                          <s-button
                            variant="secondary"
                            icon="download"
                            disabled={downloading === zipKey}
                            onClick={() => handleDownloadZip(session.sessionId)}
                          >
                            {downloading === zipKey ? 'Preparing…' : 'Download All'}
                          </s-button>
                        )}
                      </s-stack>
                    </s-grid>
                  </s-clickable>

                  {isExpanded && (
                    <div className="mu-session-expanded-content">
                      <s-stack gap="small-100" paddingBlock="small">
                        {session.files.map(file => {
                          const isOptimized = file.optimizationStatus === 'optimized';
                          const isFailed    = file.optimizationStatus === 'failed';
                          const isAborted   = file.optimizationStatus === 'cancelled';
                          const dlKey       = `${session.sessionId}:${file.safeName}`;

                          return (
                            <div key={file.safeName} className="mu-file-card">
                              <s-stack direction="inline" gap="base" alignItems="center">
                                <div style={{ position: 'relative' }}>
                                  <ManualUploadThumbnail sessionId={session.sessionId} file={file} fetchRef={fetchRef} />
                                  <div style={{ position: 'absolute', bottom: -4, right: -4, background: '#fff', borderRadius: '50%', padding: 1 }}>
                                    <s-icon
                                      source={isOptimized ? 'checkmark-circle' : isFailed ? 'alert-circle' : isAborted ? 'minus-circle' : 'clock'}
                                      tone={isOptimized ? 'success' : isFailed ? 'critical' : isAborted ? 'caution' : 'subdued'}
                                    />
                                  </div>
                                </div>

                                <s-stack gap="none" style={{ flex: 1 }}>
                                  <s-text type="strong" size="small">{file.imageName}</s-text>
                                  {isOptimized ? (
                                    <s-stack direction="inline" gap="small" alignItems="center">
                                      <s-badge size="small">Original: {formatSize(file.originalSizeKb)}</s-badge>
                                      <s-badge tone="success" size="small">Optimized: {formatSize(file.optimizedSizeKb)}</s-badge>
                                      <s-badge tone="info" size="small">Savings: {Number(file.savingsPercent).toFixed(1)}%</s-badge>
                                    </s-stack>
                                  ) : isFailed ? (
                                    <s-text color="critical" size="small">{file.errorMessage || 'Optimization failed'}</s-text>
                                  ) : isAborted ? (
                                    <s-text color="subdued" size="small">Skipped — optimization was stopped</s-text>
                                  ) : (
                                    <s-text color="subdued" size="small">In queue…</s-text>
                                  )}
                                </s-stack>

                                <s-stack direction="inline" gap="small">
                                  {isOptimized && !file.shopifyFilesExist && (
                                    <s-badge tone="critical" size="small">Removed from Shopify</s-badge>
                                  )}
                                  {file.hasCompare && (
                                    <s-button variant="secondary" size="small" icon="view" onClick={() => handleCompare(session.sessionId, file)}>
                                      Compare
                                    </s-button>
                                  )}
                                  {file.hasDownload && (
                                    <s-button
                                      variant="secondary"
                                      size="small"
                                      icon="download"
                                      disabled={downloading === dlKey}
                                      onClick={() => handleDownloadFile(session.sessionId, file)}
                                    >
                                      {downloading === dlKey ? '…' : 'Download'}
                                    </s-button>
                                  )}
                                </s-stack>
                              </s-stack>
                            </div>
                          );
                        })}
                      </s-stack>
                    </div>
                  )}
                </s-box>
              );
            })}
        </s-stack>
      )}

      <PaginationFooter
        pageInfo={pageInfo ? { ...pageInfo, totalProducts: pageInfo.totalSessions } : null}
        onPrevious={() => { setCurrentPage(p => p - 1); }}
        onNext={() => { setCurrentPage(p => p + 1); }}
        loading={loading}
        itemName="sessions"
      />
    </s-box>
  );
}

// ─── Progress section (active upload) ─────────────────────────────────────────

function UploadProgressCard({ sessionData, phase, aborting, onAbort }) {
  const progressPct = sessionData
    ? Math.round(((sessionData.completed + sessionData.failed) / sessionData.totalFiles) * 100)
    : 0;

  const isAborted  = sessionData?.status === 'aborted';
  const isRunning  = phase === 'optimizing' && !isAborted;

  return (
    <s-box padding="base" background="base" border="base" borderRadius="base">
      <s-stack gap="base">
        <s-stack direction="inline" gap="base" alignItems="center" justifyContent="space-between">
          <s-stack direction="inline" gap="base" alignItems="center">
            <s-heading>Optimization Progress</s-heading>
            {isAborted
              ? <s-badge tone="critical">Aborted</s-badge>
              : sessionData?.status === 'done'
              ? <s-badge tone="success">Completed</s-badge>
              : <s-badge tone="warning">Running</s-badge>
            }
            <s-badge tone="info">
              {sessionData?.optimizationType
                ? sessionData.optimizationType.charAt(0).toUpperCase() + sessionData.optimizationType.slice(1)
                : ''}
            </s-badge>
          </s-stack>

          {isRunning && (
            <s-stack direction="inline" gap="base" alignItems="center">
              {aborting && <s-text color="critical" type="strong">Aborting…</s-text>}
              <s-button className="abort-job-btn" onClick={onAbort} loading={aborting} disabled={aborting}>Stop Optimization</s-button>
              <div className="mu-spinner" style={{ width: aborting ? 16 : 32, height: aborting ? 16 : 32, borderWidth: aborting ? 2 : 3 }} />
            </s-stack>
          )}
        </s-stack>

        <s-stack gap="small">
          <s-stack direction="inline" justifyContent="space-between">
            <s-text>Progress: {progressPct}%</s-text>
            {isAborted && <s-text color="critical" size="small" type="strong">Aborted (Partial)</s-text>}
          </s-stack>
          <div className="progress-bar-container">
            <div
              className={`progress-bar-fill ${
                sessionData?.status === 'done'    ? 'progress-completed' :
                sessionData?.status === 'aborted' ? 'progress-failed'    :
                'progress-in-progress'
              }`}
              style={{ width: `${progressPct}%`, backgroundColor: isAborted ? '#d82c0d' : undefined }}
            />
          </div>
        </s-stack>

        <s-stack direction="inline" gap="large">
          {[
            { label: 'Total',       value: sessionData?.totalFiles,                    tone: undefined },
            { label: 'Completed',   value: sessionData?.completed,                     tone: 'success'  },
            { label: 'In Progress', value: (sessionData?.processing ?? 0) + (sessionData?.queued ?? 0), tone: 'warning'  },
            { label: 'Failed',      value: sessionData?.failed,                        tone: 'critical' },
            ...(isAborted || sessionData?.cancelled > 0
              ? [{ label: 'Aborted', value: sessionData?.cancelled ?? 0, tone: 'attention' }]
              : []),
          ].map(({ label, value, tone }) => (
            <s-stack key={label} gap="none">
              <s-text size="small" color="subdued">{label}</s-text>
              <s-heading size="small" tone={tone}>{value ?? 0}</s-heading>
            </s-stack>
          ))}
        </s-stack>
      </s-stack>
    </s-box>
  );
}

// ─── StagedFileRow ────────────────────────────────────────────────────────────
// Stable object URL per file — created once on mount, revoked on unmount.

function StagedFileRow({ file, isImage, onRemove }) {
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!isImage) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => { URL.revokeObjectURL(url); };
  }, [file, isImage]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <s-grid gridTemplateColumns={STAGED_ROW_COLS} gap="base" alignItems="center">
      <s-stack direction="inline" gap="small" alignItems="center">
        <s-box width="40px" height="40px" overflow="hidden" display="flex" alignItems="center" justifyContent="center" background="subdued" border="base" borderRadius="base">
          {previewUrl ? (
            <img src={previewUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            <s-text size="large">📄</s-text>
          )}
        </s-box>
        <s-stack gap="none">
          <s-text type="strong" size="small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.name}
          </s-text>
          <s-text tone="subdued" size="small">{formatBytesAsSize(file.size)}</s-text>
        </s-stack>
      </s-stack>
      <s-button variant="secondary" size="small" onClick={onRemove}>Remove</s-button>
    </s-grid>
  );
}

// ─── Per-file list (active upload result) ─────────────────────────────────────

// Fixed-width actions column so all rows align regardless of button content.
// Staged files use STAGED_COLS (just Remove), active/done use FILE_ROW_COLS (Compare + Download).
const FILE_ROW_COLS   = '1fr auto'; // file info | actions (auto-width)
const STAGED_ROW_COLS = '1fr auto'; // file info | remove (auto-width)

function ActiveFileList({ files, onCompare, onDownload, downloadingFile }) {
  const visible = files.filter(f =>
    ['done', 'failed', 'processing', 'cancelled'].includes(f.status)
  );

  if (!visible.length) return null;

  return (
    <s-box padding="base" border="base" borderRadius="base">
      <s-stack gap="base">
        <s-heading>Files</s-heading>

        {/* Header row */}
        <s-grid gridTemplateColumns={FILE_ROW_COLS} gap="base" alignItems="center" paddingBlock="small" borderBottom="base">
          <s-text type="strong" size="small" tone="subdued">File</s-text>
          <s-text type="strong" size="small" tone="subdued">Actions</s-text>
        </s-grid>

        {/* Data rows */}
        <s-stack gap="small">
          {visible.map((file, idx) => (
            <s-stack key={`${file.originalName}-${idx}`} gap="extra-small">

              {/* Main row */}
              <s-grid gridTemplateColumns={FILE_ROW_COLS} gap="base" alignItems="center">
                {/* Left cell — file name + status badge */}
                <s-stack direction="inline" gap="small" alignItems="center">
                  <s-text type="strong" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.originalName}
                  </s-text>
                  <s-badge tone={getFileBadgeTone(file.status)}>
                    {getFileStatusLabel(file.status)}
                  </s-badge>
                </s-stack>

                {/* Right cell — compare + download inline, or status indicator */}
                <s-stack direction="inline" gap="small" alignItems="center">
                  {file.status === 'done' && file.originalUrl && file.optimizedUrl && (
                    <s-button variant="secondary" size="small" icon="view" onClick={() => onCompare(file)}>
                      Compare
                    </s-button>
                  )}
                  {file.status === 'done' && (
                    <s-button
                      variant="secondary"
                      size="small"
                      icon="download"
                      disabled={downloadingFile === file.safeName}
                      onClick={() => onDownload(file)}
                    >
                      {downloadingFile === file.safeName ? '…' : 'Download'}
                    </s-button>
                  )}
                  {file.status === 'processing' && (
                    <s-badge>Processing…</s-badge>
                  )}
                  {file.status === 'cancelled' && (
                    <s-text tone="subdued" size="small">Skipped</s-text>
                  )}
                </s-stack>
              </s-grid>

              {/* Sub-row — size info for completed files */}
              {file.status === 'done' && (
                <s-stack direction="inline" gap="small" alignItems="center">
                  <s-badge>Original: {formatSize(file.originalSizeKb)}</s-badge>
                  <s-badge tone="success">Optimized: {formatSize(file.optimizedSizeKb)}</s-badge>
                  <s-badge tone="info">Savings: {file.savingsPercent?.toFixed(2)}%</s-badge>
                </s-stack>
              )}
              {file.status === 'failed' && file.error && (
                <s-banner tone="critical"><s-text size="small">{file.error}</s-text></s-banner>
              )}

              {idx < visible.length - 1 && <s-divider />}
            </s-stack>
          ))}
        </s-stack>
      </s-stack>
    </s-box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function ManualUploadPage() {
  const fileInputRef = useRef(null);

  const {
    session, downloading, recentsKey,
    startUpload, handleAbort, handleDownloadFile, handleDownloadZip, handleReset,
  } = useManualUpload();

  const {
    files, errors, optimizationType, isDragOver,
    setOptimizationType, addFiles, removeFile, clearAll, reset: resetFiles,
    handleDragOver, handleDragLeave, handleDrop, handleFileInputChange,
  } = useFileDropZone();

  const [compareData, setCompareData] = useState(null);

  const { phase, data, uploadError, aborting } = session;

  const doneFiles   = data?.files?.filter(f => f.status === 'done')   ?? [];
  const failedFiles = data?.files?.filter(f => f.status === 'failed') ?? [];

  const totalOriginalKb  = doneFiles.reduce((s, f) => s + (f.originalSizeKb  ?? 0), 0);
  const totalOptimizedKb = doneFiles.reduce((s, f) => s + (f.optimizedSizeKb ?? 0), 0);
  const totalSavedKb     = Math.max(0, totalOriginalKb - totalOptimizedKb);
  const overallSavingsPct = totalOriginalKb > 0
    ? ((totalSavedKb / totalOriginalKb) * 100).toFixed(1) : '0';

  const totalSelectedSize = files.reduce((s, f) => s + f.size, 0);

  const handleUploadAndOptimize = async () => {
    if (!files.length || phase !== 'idle') return;
    await startUpload(files, optimizationType);
    clearAll();
  };

  const handleFullReset = () => {
    handleReset();
    resetFiles();
    setCompareData(null);
  };

  return (
    <>
      <CompareModal data={compareData} onClose={() => setCompareData(null)} />

      <s-section>
        <s-stack gap="base">
          <s-stack direction="inline" justifyContent="space-between" alignItems="center">
            <s-stack gap="small">
              <s-heading>Manual Image Upload</s-heading>
              <s-text color="subdued">
                Upload images directly to optimize — no product sync required.
                Optimized files are available to download permanently.
              </s-text>
            </s-stack>
            {phase === 'done' && (
              <s-button variant="secondary" onClick={handleFullReset}>Upload New Images</s-button>
            )}
          </s-stack>

          {uploadError && (
            <s-banner tone="critical">
              {uploadError}
            </s-banner>
          )}

          {phase === 'idle' && (
            <>
              <s-box padding="none" border="base" borderRadius="base">
                <div
                  className={`mu-drop-zone-container${isDragOver ? ' drag-over' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, phase === 'idle')}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ACCEPTED_LABEL}
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                  <s-stack gap="base" alignItems="center">
                    <s-text size="large">🖼️</s-text>
                    <s-stack gap="small" alignItems="center">
                      <s-text type="strong">
                        {isDragOver ? 'Drop your images here' : 'Drag & drop images here'}
                      </s-text>
                      <s-text color="subdued">or click to browse — {ACCEPTED_LABEL}</s-text>
                      <s-text color="subdued" size="small">
                        Max 20 MB per file · Up to {MAX_FILES} files per session
                      </s-text>
                    </s-stack>
                  </s-stack>
                </div>
              </s-box>

              {errors.length > 0 && (
                <s-banner tone="warning">
                  <s-stack gap="small">
                    <s-text type="strong">Some files were skipped:</s-text>
                    {errors.map((err, i) => <s-text key={i} size="small">{err}</s-text>)}
                  </s-stack>
                </s-banner>
              )}

              {files.length > 0 && (
                <s-box padding="base" border="base" borderRadius="base" background="subdued">
                  <s-stack gap="base">
                    <s-stack direction="inline" justifyContent="space-between" alignItems="center">
                      <s-text type="strong">
                        {files.length} file{files.length !== 1 ? 's' : ''} selected
                        {' '}({formatBytesAsSize(totalSelectedSize)} total)
                      </s-text>
                      <s-button variant="secondary" onClick={clearAll}>Clear all</s-button>
                    </s-stack>

                    {/* Header */}
                    <s-grid gridTemplateColumns={STAGED_ROW_COLS} gap="base" alignItems="center" paddingBlock="small" borderBottom="base">
                      <s-text type="strong" size="small" tone="subdued">File</s-text>
                      <s-text type="strong" size="small" tone="subdued">Actions</s-text>
                    </s-grid>

                    <s-stack gap="small">
                      {files.map((file, index) => {
                        const isImage = file.type?.startsWith('image/');
                        return (
                          <StagedFileRow
                            key={`${file.name}-${index}`}
                            file={file}
                            isImage={isImage}
                            onRemove={() => removeFile(index)}
                          />
                        );
                      })}
                    </s-stack>

                    {files.length < MAX_FILES && (
                      <s-button variant="secondary" onClick={() => fileInputRef.current?.click()}>
                        Add More Files
                      </s-button>
                    )}
                  </s-stack>
                </s-box>
              )}

              {files.length > 0 && (
                <>
                  <s-box padding="base" border="base" borderRadius="base">
                    <s-stack gap="base">
                      <s-text type="strong">Optimization Type</s-text>
                      <s-stack direction="inline" gap="base" alignItems="center">
                        <OptimizationTypeSelector
                          optimizationType={optimizationType}
                          onChange={setOptimizationType}
                          disabled={false}
                        />
                        <s-text color="subdued" size="small">{getProfileDescription(optimizationType)}</s-text>
                      </s-stack>
                    </s-stack>
                  </s-box>

                  <s-button variant="primary" onClick={handleUploadAndOptimize}>
                    Upload &amp; Optimize {files.length} Image{files.length !== 1 ? 's' : ''}
                  </s-button>
                </>
              )}
            </>
          )}

          {phase === 'uploading' && (
            <s-box padding="base" border="base" borderRadius="base" background="subdued">
              <s-stack gap="base" alignItems="center">
                <div className="mu-spinner" />
                <s-text type="strong">Uploading images…</s-text>
                <s-text color="subdued">Sending files securely. Optimization will start automatically.</s-text>
              </s-stack>
            </s-box>
          )}

          {(phase === 'optimizing' || phase === 'done') && data && (
            <>
              <UploadProgressCard
                sessionData={data}
                phase={phase}
                aborting={aborting}
                onAbort={handleAbort}
              />
              <ActiveFileList
                files={data.files || []}
                onCompare={setCompareData}
                onDownload={handleDownloadFile}
                downloadingFile={downloading.file}
              />
            </>
          )}

          {phase === 'done' && data && doneFiles.length > 0 && (
            <s-box padding="base" border="base" borderRadius="base" background="subdued">
              <s-stack gap="base">
                <s-stack direction="inline" gap="base" alignItems="center">
                  <s-text type="strong" color="success">
                    ✓ {doneFiles.length} image{doneFiles.length !== 1 ? 's' : ''} optimized
                    {totalSavedKb > 0 && ` — saved ${formatSize(totalSavedKb)} (${overallSavingsPct}%)`}
                  </s-text>
                  {failedFiles.length > 0 && (
                    <s-badge tone="critical">{failedFiles.length} failed</s-badge>
                  )}
                </s-stack>

                {doneFiles.length > 1 ? (
                  <s-button
                    variant="primary"
                    icon="download"
                    disabled={downloading.zip || data?.status !== 'done'}
                    onClick={handleDownloadZip}
                  >
                    {downloading.zip ? 'Preparing ZIP…' : `Download All as ZIP (${doneFiles.length} files)`}
                  </s-button>
                ) : (
                  <s-button
                    variant="primary"
                    icon="download"
                    disabled={downloading.file === doneFiles[0]?.safeName}
                    onClick={() => handleDownloadFile(doneFiles[0])}
                  >
                    {downloading.file === doneFiles[0]?.safeName ? 'Downloading…' : 'Download Optimized Image'}
                  </s-button>
                )}
              </s-stack>
            </s-box>
          )}

          <RecentUploads refreshKey={recentsKey} onCompare={setCompareData} />

        </s-stack>
      </s-section>
    </>
  );
}

export default ManualUploadPage;
